'use client';

import { PromotionStar as PromotionStarIcon } from '@ahafood/icons';
import { Image } from '@ahafood/ui/components/image';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { useSearchParams, useRouter } from 'next/navigation';
import type { CampaignBanner } from '@/api/ahafood/campaign-banner';
import ImageWithFallback from '@/app/[locale]/(main)/store/components/items/fallback-image';
import { FreeDragCarousel } from '@/components/carousel/free-drag-carousel';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile/use-is-mobile';
import { useAuthStore } from '@/store';

type SuperDealSectionProps = {
  /** Banner type=SUPER_DEAL (đã lọc + sort từ API mới). */
  deals: CampaignBanner[];
  isLoading?: boolean;
  payload: {
    city_id: string;
    category_slug?: string;
    promotion?: boolean;
    special_request?: string;
    hot_deal?: boolean;
    campaign_id?: string;
  };
};

/**
 * Super Deal section — gradient yellow banner full-width edge-to-edge,
 * carousel item card compact theo style SoloMeal (w-[140px], transparent).
 * Nguồn: CampaignBanner type=SUPER_DEAL (AF-062, thay /tag). Click → trang
 * co-campaign (store list lọc server-side theo campaign_id = banner _id).
 */
export default function SuperDealSection({
  deals,
  isLoading,
  payload,
}: SuperDealSectionProps) {
  const isMobile = useIsMobile();
  const searchParams = useSearchParams();
  const router = useRouter();
  const routes = useCityRoutes();
  const { user } = useAuthStore();

  const selectedDeal = payload.campaign_id
    ? parseInt(payload.campaign_id, 10)
    : parseFloat(searchParams.get('campaign_id')!);

  const dealsToShow = deals ?? [];

  function handleDealToggle(d: number) {
    router.push(routes.listing(`co-campaign-${d}`));
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'super_deal_toggle',
      timestamp: Date.now(),
      deal_id: d,
      user_id: user?.cunstomerId,
    });
  }

  if (isLoading) {
    return (
      <section className="w-full max-w-screen-xl mx-auto pb-8">
        <div className="px-4 mb-4">
          <Skeleton className="h-8 w-44" />
        </div>
        <div className="flex flex-row flex-nowrap gap-3 px-4 overflow-x-hidden">
          {[...Array(isMobile ? 3 : 5)].map((_, index) => (
            <div
              key={`super-deal-skeleton-${index}`}
              className="basis-[150px] shrink-0 flex flex-col gap-1.5"
            >
              <Skeleton className="aspect-square w-full rounded-2xl" />
              <Skeleton className="h-4 w-3/4 mt-1" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (dealsToShow.length === 0) return null;

  return (
    <section className="w-full max-w-screen-xl mx-auto pb-8">
      {/* Header */}
      <div className="px-4 mb-4">
        <Image
          src={'/static/icons/super-deal.svg'}
          alt="Super deal"
          width={180}
          height={31}
          className="object-contain object-bottom"
        />
      </div>

      {/* Cards carousel — full-width bleed, không bị cắt bởi padding */}
      <FreeDragCarousel className="w-full" contentClassName="-ml-3 pl-4 pr-4">
        {dealsToShow.map((d, index) => {
          const isSelected = d._id === selectedDeal;
          const isLast = index === dealsToShow.length - 1;
          return (
            <FreeDragCarousel.Item
              key={`deal-${d._id}-${index}`}
              className={cn(
                'basis-[150px] pl-3',
                isLast && 'basis-[166px] pr-4'
              )}
            >
              <button
                type="button"
                onClick={() => handleDealToggle(d._id)}
                className="group flex w-full min-w-0 flex-col gap-1.5 text-left"
              >
                <div
                  className={cn(
                    'relative aspect-square w-full overflow-hidden rounded-2xl transition-all',
                    isSelected
                      ? 'ring-2 ring-primary-50 ring-offset-2'
                      : 'group-hover:opacity-90'
                  )}
                >
                  <ImageWithFallback
                    src={
                      d.image_url ??
                      `${assetPrefix}/static/images/empty-store.webp`
                    }
                    alt={d.description_vi_vn ?? d.title_vi_vn}
                    fill
                    style={{ objectFit: 'cover' }}
                    fallbackSrc={`${assetPrefix}/static/images/empty-store.webp`}
                  />
                </div>
                <div className="w-full min-w-0 px-1">
                  <h3 className="line-clamp-2 text-xs font-bold text-neutral-90 leading-tight min-h-8">
                    {d.title_vi_vn}
                  </h3>
                  <div className="flex items-center gap-1 text-[11px] text-neutral-60 w-full min-w-0">
                    <PromotionStarIcon className="size-3 shrink-0 text-amber-500" />
                    <span className="truncate min-w-0">
                      {d.description_vi_vn}
                    </span>
                  </div>
                </div>
              </button>
            </FreeDragCarousel.Item>
          );
        })}
      </FreeDragCarousel>
    </section>
  );
}
